import {
  HStack,
  SubAccountTypeEnum,
  Text,
  TreeViewTooltip,
  baseTreeGroupColumnDef,
  displayNameComparator,
  getDisplayNameOrNameLabel,
  getFirstParentsPathForSubAccount,
  useConstant,
  useDynamicCallback,
  type Position,
} from '@talos/kyoko';
import { autoGroupSortingDefaults } from '@talos/kyoko/src/components/BlotterTable/columns/autoGroupSortingDefaults';
import type {
  GridApi,
  GridOptions,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { compact, throttle } from 'lodash-es';
import { useMemo } from 'react';
import { useUpdateEffect } from 'react-use';
import { useRollupTreeRef } from '../../../../hooks';
import { useSubAccountRollupMemberships, useSubAccounts } from '../../../../providers';

interface UseHierarchicalVariantOptionsParams {
  /**
   * Whether or not the blotter is in hierarchical mode (as in, whether or not the code paths in here will be used)
   */
  hierarchical: boolean;
  /**
   * Grabbed from useBlotterTable, a function which refreshes the row model of AgGrid entirely
   */
  refreshClientSideRowModel: GridApi['refreshClientSideRowModel'] | undefined;
}

interface UseHierarchicalVariantOptionsOutput {
  gridOptionsOverlay: Pick<GridOptions, 'getDataPath' | 'autoGroupColumnDef' | 'showOpenedGroup' | 'treeData'>;
}

/** Sub-account tree levels in hierarchy paths resolve display labels via subAccountsByName before the leaf rowID segment. */
export const POSITIONS_SUB_ACCOUNT_HIERARCHY_DISPLAY_NAME_CONTEXT_KEYS = Array.from(
  { length: 8 },
  () => 'subAccountsByName' as const
);

export const useHierarchicalVariantSpecs = ({
  refreshClientSideRowModel,
  hierarchical,
}: UseHierarchicalVariantOptionsParams): UseHierarchicalVariantOptionsOutput => {
  const { rollupMembershipsByChildParent } = useSubAccountRollupMemberships();
  const { subAccountsByName, subAccountsByID } = useSubAccounts();
  // Stable version of the rollup tree since it is passed as a dep to a column definition. When its a ref it doesnt cause the blotter to remount on change.
  const rollupTreeRef = useRollupTreeRef();

  // Stable function
  // This has to be a useDynamicCallback instead of a useCallback because AgGrid doesn't seem to
  // care about any prop changes of this function. So the right thing to do is pass it as a stable function.
  const getDataPath = useDynamicCallback((node: Position) => {
    if (!node.SubAccount) {
      return [node.rowID];
    }
    const subAccountID = subAccountsByName?.get(node.SubAccount)?.SubaccountID;
    if (!subAccountID) {
      return [node.SubAccount, node.rowID];
    }

    const pathOfIds = getFirstParentsPathForSubAccount(subAccountID, rollupMembershipsByChildParent);
    const pathOfNames = compact(pathOfIds.map(id => subAccountsByID?.get(id)?.Name)).reverse();
    return [...pathOfNames, node.SubAccount, node.rowID];
  });

  // When the Rollup Memberships change, we need to have the blotter rebuild the tree.
  // This is the simplest way of doing this. Whenever the underlying data of the tree changes, make AgGrid recompute its entire row model.
  // The only alternative is to have the tree path of each Position stitched onto the Position entity itself. If done this way, the grid would react
  // on its own and perform delta updates on affected rows. This should be more performant, but much more complex, and not deemed to be needed yet.
  // Also, rollup memberships will only change when someone is actually editing them. It is understandable that the app has to recompute things when the model changes.
  // Clients can work around this if we start hitting performance issues.
  const throttledFunc = useConstant(
    throttle(() => refreshClientSideRowModel?.(), 1000, { leading: true, trailing: true })
  );
  useUpdateEffect(() => {
    // Only do this work if the blotter is in hierarchical mode (sometimes i wish you could just call hooks conditionally)
    if (rollupMembershipsByChildParent && hierarchical) {
      throttledFunc();
    }
  }, [rollupMembershipsByChildParent, throttledFunc, hierarchical]);

  const autoGroupColumnDef = useMemo(
    () =>
      ({
        ...baseTreeGroupColumnDef,
        ...autoGroupSortingDefaults,
        valueFormatter: ({ data, context, node }: ValueFormatterParams<Position>) => {
          const subAccountName = data?.SubAccount ?? node?.key;
          if (!subAccountName) {
            return 'Unknown';
          }

          const subAccount = context.current.subAccountsByName?.get(subAccountName);
          return getDisplayNameOrNameLabel(subAccount) || String(subAccountName);
        },

        comparator: (valueA, valueB, nodeA) =>
          displayNameComparator(valueA, valueB, nodeA, POSITIONS_SUB_ACCOUNT_HIERARCHY_DISPLAY_NAME_CONTEXT_KEYS),

        // This filterValueGetter is used to get the filtering context menu item in the right-click menu
        filterValueGetter: ({ data, node }: ValueGetterParams<Position>) => {
          // for the astute reader :), node.key can indeed be a rollup here, but it wont be present in the filterable property's options,
          // so it doesnt matter
          return data?.SubAccount ?? node?.key;
        },
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: ({ valueFormatted, value, context }: ICellRendererParams) => {
            const subAccount = value ? context.current.subAccountsByName?.get(value) : undefined;
            const renderTree =
              subAccount != null && subAccount.Type === SubAccountTypeEnum.Rollup && rollupTreeRef.current != null;

            return (
              <HStack gap="spacing8" alignItems="center">
                <Text>{valueFormatted}</Text>
                {renderTree && (
                  <TreeViewTooltip
                    tree={rollupTreeRef.current}
                    subAccount={subAccount}
                    highlight={[subAccount.SubaccountID.toString()]}
                  />
                )}
              </HStack>
            );
          },
        },
        headerName: 'Sub Account',
        initialSort: 'asc',
      }) satisfies GridOptions['autoGroupColumnDef'],
    [rollupTreeRef]
  );

  const output: UseHierarchicalVariantOptionsOutput = useMemo(
    () => ({
      gridOptionsOverlay: {
        getDataPath,
        autoGroupColumnDef,
        showOpenedGroup: true,
        treeData: true,
      },
    }),
    [getDataPath, autoGroupColumnDef]
  );

  return output;
};
